import { AddressCreateParams, ContactCreateParams } from '@brenger/api-client';
import { useDebounce, useForm } from '@brenger/react';
import * as React from 'react';
import { AddressFieldsProps } from '.';
import { Button, Col, InputText, Row } from '../../../../../brenger-shared-ui';
import { useTypedSelector } from '../../../../../hooks';
import { translate } from '../../../../../utils/localization';
import { validateLine1, validateRequired, warnLine1 } from '../../../../../utils/validation';
import { actions, getStopDestinationDetails } from '../../../ducks';

import { Translate } from 'react-localize-redux-dep-updated';
import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import bookBlue from '../../../../../brenger-shared-ui/assets/images/user-group_blue.svg';
import bookWhite from '../../../../../brenger-shared-ui/assets/images/user-group_white.svg';
import { AddressBook } from '../../../../../brenger-shared-ui/components/form/AddressBook';
import { SheetModal } from '../../../../../brenger-shared-ui/components/layout/sheetlayout';
import { CacheKey } from '../../../../../types';
import { autocompleteAddress } from '../../../../../utils/geo';
import { isUserLoggedIn } from '../../../../User/ducks';

export const AddressFieldsOther: React.FC<AddressFieldsProps> = ({
  forceShowErrors,
  type,
  setLoading,
  setIsFormError,
}) => {
  const existing = useTypedSelector(state => state.generalTransport.contact[type]);
  const widgetAddress = useTypedSelector(state => getStopDestinationDetails(state, type));
  const dispatch = useDispatch();
  const showAddressBook = useTypedSelector(isUserLoggedIn);
  const [showModal, setShowModal] = React.useState(false);

  const form = useForm({
    persist: {
      key: `gf_contact_address_${type}`,
      type: 'session',
    },
    initialState: {
      isPrefilled: existing?.address_is_prefilled || false,
      postal_code: existing?.address?.postal_code || widgetAddress?.postal_code || '',
      line1: existing?.address?.line1 || [widgetAddress?.line1, widgetAddress?.line2].filter(Boolean).join(' ') || '',
      locality: existing?.address?.locality || widgetAddress?.locality || '',
    },
    validators: {
      postal_code: val => validateRequired(translate('form.fields.address.postal_code.label'), val),
      line1: validateLine1,
      locality: val => !val,
    },
  });

  const line1 = form.data.line1.value;
  const postalCode = form.data.postal_code.value;
  const autoCompleteInput = `${line1}${postalCode}`;
  const debouncedAutocompleteInput = useDebounce(autoCompleteInput, 2500);
  const isTyping = !form.data.isPrefilled.value && autoCompleteInput !== debouncedAutocompleteInput;

  const autocomplete = useQuery({
    queryKey: [CacheKey.RETRIEVE_GEO_LOCATION, debouncedAutocompleteInput],
    queryFn: () =>
      autocompleteAddress({
        line1,
        postal_code: postalCode,
        locality: form.data.locality.value,
        country_code: widgetAddress?.country_code || 'NL',
      }),
    enabled:
      !!debouncedAutocompleteInput &&
      !form.getError('postal_code') &&
      !form.getError('line1') &&
      !form.data.isPrefilled.value,
  });

  React.useEffect(() => {
    if (autocomplete.data) {
      form.set({
        locality: autocomplete.data.locality,
      });
      const { latitude, longitude, ...rest } = autocomplete.data;
      dispatch(
        actions.setStopAddress({
          stopType: type,
          address: {
            ...rest,
            line1,
            line2: undefined,
            lat: latitude,
            lng: longitude,
          } as AddressCreateParams,
        })
      );
    } else if (!form.data.isPrefilled.value) {
      dispatch(actions.setStopAddress({ stopType: type, address: null }));
    }
  }, [autocomplete.data]);

  /**
   * Sync loading state to parent
   */
  React.useEffect(() => {
    setLoading(autocomplete.isFetching || isTyping);
  }, [autocomplete.isFetching, isTyping]);

  /**
   * Sync error state to parent
   */
  React.useEffect(() => {
    setIsFormError(form.hasErrors);
  }, [form.hasErrors]);

  const onHistoricalChange = (address: AddressCreateParams): void => {
    form.set({
      isPrefilled: true,
      line1: address.line1,
      postal_code: address.postal_code,
      locality: address.locality,
    });
    dispatch(actions.setStopAddress({ stopType: type, address }));
  };

  return (
    <>
      <Row>
        <Col xs={12}>
          {showAddressBook && (
            <Button
              buttonStyle="outline"
              fullWidth={true}
              type="button"
              extraClasses="address-book--contact-button"
              onClick={() => setShowModal(true)}
            >
              <span className="flex justify-between items-center w-full">
                <Translate id="address_book.list_action" />
                <div style={{ width: '20px', height: '20px' }}>
                  <img className="address-book--contact-button-icon-blue" src={bookBlue} />
                  <img className="address-book--contact-button-icon-white" src={bookWhite} />
                </div>
              </span>
            </Button>
          )}
        </Col>
        <Col xs={12} extraClasses="pb-[1em]">
          <InputText
            value={form.data.line1.value}
            onChange={l => form.set({ isPrefilled: false, line1: l })}
            id={`${type}_line1`}
            placeholder={translate('form.fields.address.line1.label')}
            meta={{
              error: form.getError('line1'),
              warning: warnLine1(form.data.line1.value),
              showError: forceShowErrors || form.data.line1.isDirty,
            }}
          />
        </Col>
        <Col xs={12} sm={4} extraClasses="pb-[1em]">
          <InputText
            value={form.data.postal_code.value}
            onChange={postal_code => form.set({ isPrefilled: false, postal_code: postal_code.trim() })}
            id={`${type}_postal_code`}
            placeholder={translate('form.fields.address.postal_code.label')}
            meta={{
              error: form.getError('postal_code'),
              showError: forceShowErrors || form.data.postal_code.isDirty,
            }}
          />
        </Col>
        <Col xs={12} sm={8} extraClasses="pb-[1em]">
          <InputText
            value={form.data.locality.value}
            id={`${type}_locality`}
            disabled={true}
            placeholder={translate('form.fields.address.locality.label')}
          />
        </Col>
      </Row>
      <SheetModal isActive={showModal} closeHandler={() => setShowModal(false)}>
        <AddressBook
          handleClose={() => setShowModal(false)}
          onSelect={addr => {
            setShowModal(false);
            const { latitude, longitude, ...rest } = addr.address;
            onHistoricalChange({
              ...rest,
              lat: latitude,
              lng: longitude,
            } as AddressCreateParams);
            if (addr.contact)
              dispatch(
                actions.setContactDetails({
                  contact_type: type,
                  details: addr.contact as ContactCreateParams,
                })
              );
            if (addr.instructions)
              dispatch(
                actions.setStopAddress({
                  stopType: type,
                  instructions: addr.instructions,
                })
              );
          }}
        />
      </SheetModal>
    </>
  );
};
